import { computed, effect, inject, Injectable } from '@angular/core';
import { OrderApiResponseStateService } from './order-api-response-state.service';
import { BasePdResponseService } from './base-pd-response.service';
import { AdditionalOfferData, OfferType } from '../../../../../models/order/workflow/additional-offer.model';
import { Customization, FlattenCustomization } from '../../../../../models/order/api-models/order-pd-response.model';
import { VideoPackageTypeEnum } from '../../../../../enums/video-package-type.enum';
import { OrderFlowType } from '../../../../../models/order/order.model';

@Injectable({ providedIn: 'root' })
export class VideoProductResponseService extends BasePdResponseService {
    private orderApiResponseStateService = inject(OrderApiResponseStateService);
    override productResponse = computed(() => this.orderApiResponseStateService.videoProductDetailsData());
    updateOfferResponse = computed(() => {
        return this.orderApiResponseStateService.videoUpdateOfferData();
    });

    getATVProductComponent() {
        const pdResponse = this.productResponse();
        return pdResponse?.Components?.Component.find(c => c.ProductType?.toUpperCase() === VideoPackageTypeEnum.ATV);
    }

    getDTVProductComponent() {
        const pdResponse = this.productResponse();
        return pdResponse?.Components?.Component.find(c => c.ProductType?.toUpperCase() === VideoPackageTypeEnum.DTV);
    }

    getUpdateOfferProductComponent() {
        const updateOfferResponse = this.updateOfferResponse();
        return updateOfferResponse?.Components?.Component;
    }

    getSelectedComponent() {
        return this.getATVProductComponent() || this.getDTVProductComponent();
    }

    isSatellitePackageSelected(): boolean {
        return this.getDTVProductComponent() != null;
    }

    isStreamPackageSelected(): boolean {
        return this.getATVProductComponent() != null;
    }

    genrePackageSelected(): boolean {
        const pdResponse = this.getATVProductComponent();
        if (!pdResponse) return false;

        return pdResponse?.ComponentCode?.includes('GENRE');
    }

    findCustomizationByCode(customizationCode: string): Customization | undefined {
        const component = this.getSelectedComponent();
        if (!component?.Customizations?.Customization || component.Customizations.Customization.length === 0) return undefined;

        const customizations = component.Customizations.Customization;
        return this.findNestedCustomization(customizations, customizationCode);
    }

    getDeliveryAndHandlingFee(): { name: string; amount: number; disclosure: string } | null {
        const component = this.getUpdateOfferProductComponent();
        if (!component) return null;

        const customizations = component.Customizations?.Customization;
        if (!customizations) return null;

        const deliveryCustomization = customizations
            .find(c => c.CustomizationCode === 'ATV-DELIVERY-CHARGES');

        if (!deliveryCustomization) return null;

        const deliveryChoice = deliveryCustomization.Choices?.Choice?.[0];
        if (!deliveryChoice?.Fee?.Fee) return null;

        return {
            name: deliveryChoice.Name ? `${deliveryChoice.Name} Fee` : 'Delivery and Handling Fee',
            amount: deliveryChoice.Fee.Fee,
            disclosure: deliveryChoice.CartDisclosure || ''
        };
    }

    constructor() {
        super();
        effect(() => {
            const pdResponse = this.productResponse();
            if (pdResponse) {
                this.setupCustomizations();
            } else {
                this.customizations.set([]);
            }
        });
    }

    override setupCustomizations() {
        const tempCustomizations = []
        const updateOfferCustomizations = this.getUpdateOfferProductComponent();
        if (updateOfferCustomizations) {
            const customizations = this.flattenCustomizations(updateOfferCustomizations, OrderFlowType.Video);
            tempCustomizations.push(...customizations);
        }

        const pdCustomizations = this.getSelectedComponent();
        if (!pdCustomizations) return;
        const customizations = this.flattenCustomizations(pdCustomizations, OrderFlowType.Video);

        customizations?.forEach(cust => {
            const isExists = tempCustomizations.some(ecust => ecust.customization.CustomizationCode === cust.customization.CustomizationCode);
            if (!isExists) {
                tempCustomizations.push(cust);
            }
        });
        tempCustomizations.sort((a, b) => a.customization.Seq - b.customization.Seq);
        this.customizations.set(tempCustomizations);
    }

    getABPResidentialStreamAdditionalOfferData(abpEnabled: boolean): AdditionalOfferData {
        const abpChoiceCode = abpEnabled ? 'ATT-VIDEO-ABP-BOTH-YES' : 'ATT-VIDEO-ABP-PAPERLESS-YES';
        return {
            choiceCode: abpChoiceCode,
            customizationCode: 'VIDEO-INCLUDED-OFFERS-REQUIRED',
            type: OfferType.Required,
            usoc: abpChoiceCode
        }
    }

    getABPResidentialSatelliteOfferData(abpEnabled: boolean): AdditionalOfferData {
        const abpChoiceCode = abpEnabled ? 'ATT-DTV-ABP-YES' : 'ATT-DTV-ABP-NO';
        return {
            choiceCode: abpChoiceCode,
            customizationCode: 'ATT-DTV-ABP',
            type: OfferType.Required,
            usoc: abpChoiceCode
        }
    }


    getAutoBillPayChoiceCodes(): { both?: string; paperless?: string; customizationCode?: string } {
        const customization = this.getCustomizations()?.find(c => c.customization.DetailedGroupName === 'Auto Bill Pay')?.customization;
        const choices = customization?.Choices?.Choice ?? [];
        return {
            both: choices.find(choice => choice.ChoiceSubGroupName === 'Both')?.ChoiceCode,
            paperless: choices.find(choice => choice.ChoiceSubGroupName === 'Paperless')?.ChoiceCode,
            customizationCode: customization?.CustomizationCode
        }
    }

    getABPAutoBillPayOfferData(): AdditionalOfferData {
        const { both, customizationCode } = this.getAutoBillPayChoiceCodes();
        return {
            choiceCode: both,
            customizationCode,
            type: OfferType.Required,
            accepted: true,
            usoc: both
        }
    }

    override getDefaultSelectedChoices(): AdditionalOfferData[] {
        const pdResponse = this.productResponse();
        if (pdResponse) this.setupCustomizations();
        const allCustomizations = this.getCustomizations();
        const defaultChoices = [];

        allCustomizations.forEach(cust => {
            cust.customization.Choices?.Choice?.forEach((choice) => {
                if (choice?.IncludedWithPackage === true || choice?.IncludedWithPackage === 'true' || choice?.DefaultSelected === true || choice.ChoiceCode === 'ATT-DTV-ABP-YES') {
                    defaultChoices.push(choice);
                }
            });
        });

        const pdDefaultOffers: AdditionalOfferData[] = []
        for (const offer of defaultChoices) {
            const customization = this.findCustomizationByChoiceCode(offer.ChoiceCode);
            const type = customization?.customization.SubGroupName === 'IncludedOffers' ? OfferType.Required : OfferType.Additional;
            pdDefaultOffers.push({ customizationCode: customization?.customization.CustomizationCode, choiceCode: offer.ChoiceCode, type, accepted: true, usoc: offer.Usoc });
        }
        return pdDefaultOffers;
    }

    isIncludedWithPackage(cust: FlattenCustomization): boolean {
        return cust.customization.Choices?.Choice?.some(choice => choice.IncludedWithPackage);
    }

    findUpdateOfferCustomizationByCode(customizationCode: string): Customization | undefined {
        const component = this.getUpdateOfferProductComponent();
        if (!component?.Customizations?.Customization || component.Customizations.Customization.length === 0) return undefined;

        const customizations = component.Customizations.Customization;
        return this.findNestedCustomization(customizations, customizationCode);
    }

    hasDTVCommercialSatelliteABP(): boolean {
        const abp = this.findUpdateOfferCustomizationByCode('DTV-VIDEO-ABP-REQ');
        return abp ? true : false;
    }

}